/* What the card's tabs share, so they read as one system: the section-heading pieces ("Read-only",
   the window and the way to the full list — `Page.js`), the link click that never opens the row's
   modal and goes through the card's `leaveTo`, which authors have a profile to link to, a tab's
   failed load (`LoadError`), the signed balance (`money`, the list's too), and the document cells
   (number + date, point, author, visit, a filling text cell — Orders, Returns, Payments, Debts;
   `PersonLink` — Addresses). */

import React from "react";
import { Link } from "react-router-dom";
import { Avatar, Button, Empty, Flex, Tag, Tooltip, Typography } from "antd";
import { LinkOutlined, LockOutlined } from "@ant-design/icons";

import { PersonCell, dash, fullName, initialsOf, textOf } from "../../component/table-cells";
import { COUNTERPARTIES_PAGE } from "../../config/lang";
import dayjs from "../../util/dayjs";
import { formatMoney } from "../../util/formatter";
import { translate } from "../../util/util";

/* Only these two roles have a profile page to go to; any other author (sales representative,
   order manager, a supervisor manager) is named as text — the app has no page for them. */
const PROFILE_PATH = { merchandiser: "/merchandiser", supervisor: "/supervisors" };

/* A link's click must not ALSO open the row's modal. A modified click (new tab / window) is left to
   the browser — it leaves nothing behind, so there is nothing to ask about. */
export const linkClick = (leaveTo, to) => event => {
  event.stopPropagation();
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  leaveTo(event, to);
};

/** "Read-only", beside the section title: it qualifies the section itself, so it sits with its
    name rather than with the controls on the right. */
export function ReadOnlyTag({ lang }) {
  return (
    <Tag className="cp-readonly" icon={<LockOutlined />}>
      {translate(COUNTERPARTIES_PAGE, "docs_readonly", lang)}
    </Tag>
  );
}

/** The right half of the section heading: the window, and the way to the rest of this
    counterparty's documents — `listPath` (`/sales/orders`, `/sales/returns`), labelled `allKey`.
    Without a `listPath` (Payments, Debts: no list of their own to go to) only the window is shown.
    `windowKey` names that window when it is not the last 90 days (Debts: "open debts"). */
export function TabHead({ counterpartyId, counterpartyName, lang, leaveTo, listPath, allKey, windowKey = "docs_window" }) {
  const t = key => translate(COUNTERPARTIES_PAGE, key, lang);
  /* All of them, not the last 90 days: no dates, which the full list reads as "all time" for a
     counterparty link. The name rides in state so its chip and field are labelled without a lookup. */
  const all = {
    pathname: listPath,
    search: `?counterparty=${counterpartyId}`,
    state: { counterpartyId, counterpartyName },
  };
  return (
    <Flex className="cp-sec__extra" align="center" gap="small" wrap>
      <Typography.Text type="secondary">{t(windowKey)}</Typography.Text>
      {/* An in-app navigation: `LinkOutlined`, as `CounterpartySection`'s own "all orders" button —
          `ExportOutlined` is reserved for leaving the app (spec/02). */}
      {listPath ? (
        <Button icon={<LinkOutlined />} onClick={event => leaveTo(event, all)}>
          {t(allKey)}
        </Button>
      ) : null}
    </Flex>
  );
}

/** A tab's failed load, with its Retry — the Documents, Addresses and Contacts tabs alike. */
export function LoadError({ lang, onRetry }) {
  const t = key => translate(COUNTERPARTIES_PAGE, key, lang);
  return (
    <Empty
      image={Empty.PRESENTED_IMAGE_SIMPLE}
      description={
        <Flex vertical gap={8} align="center">
          <Typography.Text strong>{t("tab_load_error")}</Typography.Text>
          <Button size="small" onClick={onRetry}>
            {t("retry")}
          </Button>
        </Flex>
      }
    />
  );
}

/* ------------------------------------------------------------------ cells */

/* `remaining_funds` is a signed BALANCE and arrives as a string ("-187559.23"). `formatMoney`
   runs it through `Number()`, so the minus survives; it is never relabelled as debt nor shown
   unsigned — that would invert the one figure a supervisor acts on (`CounterpartySection.js`).
   A negative balance is also set in `--danger-700` so it stands out in a column of figures; the
   minus sign stays, so colour is never the only signal (spec/01). */
export const money = value => {
  if (value === null || value === undefined || value === "") return dash;
  return <span className={Number(value) < 0 ? "lp-num cp-neg" : "lp-num"}>{formatMoney(value)}</span>;
};

/** The number on top, linking to the document's page (`to`); the date and time under it. */
export function NumberCell({ row, to, leaveTo }) {
  const when = row.datetime ? dayjs(row.datetime).format("DD.MM.YYYY HH:mm") : null;
  return (
    <Flex vertical className="lp-main">
      <Link className="cp-doclink" to={to} title={textOf(row.name)} onClick={linkClick(leaveTo, to)}>
        {textOf(row.name)}
      </Link>
      {when ? (
        <Typography.Text type="secondary" className="lp-main__sub" ellipsis={{ tooltip: when }}>
          {when}
        </Typography.Text>
      ) : null}
    </Flex>
  );
}

/** One line of text that fills its whole column, up to a small gap before the next one, and is cut
    only where the column ends — on a wide card that shows the rest of the text rather than a
    cut-off half and an empty run after it. The column's own width is still sized by content: the
    hidden copy (`full`, also the tooltip) stands for the text at its usual 280px cap (`lp-fit`),
    while the visible line has no intrinsic width of its own (`.cp-fill`). `type` is
    `Typography.Text`'s (the Payments tab's muted comment). */
export function FillCell({ full, type, children }) {
  return (
    <div className="cp-fill">
      <span className="cp-fill__size" aria-hidden="true">
        {full}
      </span>
      <Typography.Text className="cp-fill__text" type={type} ellipsis={{ tooltip: full }}>
        {children}
      </Typography.Text>
    </div>
  );
}

/** The full list's one-line "name — address", the name linking to the point, in a `FillCell`. The
    point comes through the document's visit, so one without a visit has none. */
export function PointCell({ row, leaveTo }) {
  if (!row.point) return dash;
  const to = `/points/${row.point.id}`;
  const full = [row.point.name, row.point.address].filter(Boolean).join(" — ");
  return (
    <FillCell full={full}>
      <Link className="cp-doclink cp-doclink--inline" to={to} onClick={linkClick(leaveTo, to)}>
        {textOf(row.point.name)}
      </Link>
      {row.point.address ? <Typography.Text type="secondary"> — {row.point.address}</Typography.Text> : null}
    </FillCell>
  );
}

/** The full list's `PersonCell` (avatar + name); the name links to a profile for the two roles
    that have one (`created_by_role`). */
export function AuthorCell({ row, leaveTo }) {
  const name = fullName(row.created_by);
  if (!name) return dash;
  const base = PROFILE_PATH[row.created_by_role];
  if (!base) return <PersonCell user={row.created_by} strong={false} />;
  return <PersonLink user={row.created_by} to={`${base}/${row.created_by.id}`} leaveTo={leaveTo} />;
}

/** `PersonCell`'s avatar + name, the name a link to `to` through the card's `leaveTo` — the Orders
    tab's author, the Addresses tab's responsible merchandiser. */
export function PersonLink({ user, to, leaveTo }) {
  const name = fullName(user);
  return (
    <Flex align="center" gap={8} className="lp-main">
      <Avatar size={28} className="lp-who__av" style={{ fontSize: 12 }} src={user.photo || null}>
        {initialsOf(name)}
      </Avatar>
      <Link className="cp-doclink cp-doclink--plain" to={to} title={name} onClick={linkClick(leaveTo, to)}>
        {name}
      </Link>
    </Flex>
  );
}

/** "Visit DD.MM" linking to the visit, or a muted "no visit". A row without `visit_date` (a visit
    with no start) reads plain "Visit". */
export function VisitCell({ row, lang, leaveTo }) {
  const t = key => translate(COUNTERPARTIES_PAGE, key, lang);
  if (!row.visit)
    return (
      <Typography.Text type="secondary" ellipsis={{ tooltip: t("o_no_visit") }}>
        {t("o_no_visit")}
      </Typography.Text>
    );
  const to = `/visit/${row.visit}`;
  const day = row.visit_date ? dayjs(row.visit_date) : null;
  const link = (
    <Link className="cp-doclink cp-doclink--plain" to={to} onClick={linkClick(leaveTo, to)}>
      {/* `LinkOutlined`, as the visit link in `/sales/orders` and in the order itself. */}
      {day ? t("o_visit_on").replace("{date}", day.format("DD.MM")) : t("o_visit")} <LinkOutlined />
    </Link>
  );
  /* DD.MM in the cell; the year in the tooltip, since 90 days can cross New Year. */
  return day ? <Tooltip title={day.format("DD.MM.YYYY")}>{link}</Tooltip> : link;
}
